import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { filter, finalize, first, take, takeUntil } from 'rxjs/operators';

import { AnalyticsService } from './common/services/analytics-wrapper/analytics.service';
import { CommonUtility } from './common/utils/commonUtils';
import { FooterComponent } from './common/components/organisms/footer/footer.component';
import { HeaderComponent } from './common/components/organisms/header/header.component';
import { LandingLayoutComponent } from './common/components/organisms/landing-layout/landing-layout.component';
import { MonitoringService } from './common/services/app-monitoring-service/app-monitoring.service';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ConfirmationDialogService } from './common/services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmationDialogComponent } from './common/components/molecules/confirmation-dialog/confirmation-dialog.component';

import { ToastService } from './common/services/toast/toast.service';
import { ToastComponent } from './common/components/molecules/toast/toast.component';
import { RenderLabelPipe } from './common/pipes/render-label/render-label.pipe';
import { LoaderComponent } from './common/components/atoms/loader/loader.component';
import { LoginService } from './common/services/login-service/login.service';
import { SESSION_STORAGE_KEYS } from './common/constants/storage.constant';
import { SessionStorageUtility } from './common/utils/sessionStorageUtils';
import { BannerComponent } from './common/components/molecules/banner/banner.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    LandingLayoutComponent,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    ConfirmationDialogComponent,
    ToastComponent,
    RenderLabelPipe,
    LoaderComponent,
    BannerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy, OnInit {
  ngUnsubscribe = new Subject<void>();
  isIframe = false;
  windowScrolled = false;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly monitoringService: MonitoringService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    public confirmationDialogService: ConfirmationDialogService,
    public toastService: ToastService,
    public loginService: LoginService,
  ) {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(() => {
        this.analyticsService.pageView();
        window.scrollTo(0, 0);
      });
    this._logNavChangePerf();
    this.addWhatFixScripts();
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    this.checkAndRedirectForSSO();
    // polling is started reactively by MaintenanceService.activateImmediately() on a 503
  }

  /**
   * When coming from SSO login, check for redirectUri in session,
   * if present then navigate to selected page
   */
  checkAndRedirectForSSO(): void {
    this.loginService.isLoggedInSub
      .pipe(
        filter((isLoggedIn: boolean) => isLoggedIn),
        take(1),
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          SessionStorageUtility.clearItem(SESSION_STORAGE_KEYS.ssoRedirectUri);
        }),
      )
      .subscribe(() => {
        const redirectUri: string = SessionStorageUtility.get(
          SESSION_STORAGE_KEYS.ssoRedirectUri,
        );
        SessionStorageUtility.clearItem(SESSION_STORAGE_KEYS.ssoRedirectUri);
        // checking if url is valid or not
        if (
          redirectUri?.startsWith('/') &&
          redirectUri?.split('/')?.length >= 2 &&
          !!redirectUri?.split('/')?.[1]
        ) {
          this.router.navigateByUrl(redirectUri);
        }
      });
  }

  /**
   * listen for route changes to log route change metrics
   */
  private _logNavChangePerf(): void {
    this.router.events
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          const routeUrl: string = event.url;
          if (!CommonUtility.isUatEnv() && !CommonUtility.isProdEnv()) {
            console.time(routeUrl);
            console.timeLog(routeUrl);
          }
          this.monitoringService.startNavigationEvent(routeUrl);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationError
        ) {
          if (!CommonUtility.isUatEnv() && !CommonUtility.isProdEnv()) {
            console.timeEnd(event.url);
          }
          this.monitoringService.endNavigationEvent(event.url);
        }
      });
  }

  /**
   * add whatfix scripts after fist navigation completes
   */
  addWhatFixScripts(): void {
    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationEnd),
        first(),
      )
      .subscribe(() => this.setExternalScript());
  }

  private setExternalScript(): void {
    const externalScriptsSrc: string[] = [
      '/assets/scripts/external-scripts.js',
    ];
    externalScriptsSrc.forEach((src: string) => {
      if (document.body.querySelector(`script[src="${src}"]`)) {
        return;
      }
      const scriptTag: HTMLScriptElement =
        this.renderer.createElement('script');
      this.renderer.setAttribute(scriptTag, 'src', src);
      this.renderer.setAttribute(scriptTag, 'type', 'text/javascript');
      this.renderer.setAttribute(scriptTag, 'defer', 'true');
      this.renderer.appendChild(document.body, scriptTag);
    });
  }

  @HostListener('window:scroll')
  @HostListener('window:shadowScroll', ['$event'])
  onWindowScroll(event?: any) {
    if (event) {
      this.windowScrolled = event.detail.scrollTarget.scrollTop >= 100;
    } else {
      if (window.pageYOffset || document.body.scrollTop > 100) {
        this.windowScrolled = true;
      } else if (
        (this.windowScrolled && window.pageYOffset) ||
        document.body.scrollTop < 10
      ) {
        this.windowScrolled = false;
      }
    }
    this.cdr.detectChanges();
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  /**
   * ngOnDestroy lifecycle hook
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
